import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Segmented, Tag, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { Button } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { DatePickerField } from '@/shared/ui/components';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { TagSearchField } from '@/settings/components/tag-search-field';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  useLanguageSearch,
  useLicenseAuthorities,
  useProcedureCodesSearch,
  useProfessionalDetails,
  useSaveProfessionalDetails,
} from '@/settings/services';
import { SpecialityPicker } from '@/settings/components/speciality-picker';
import { SignatureCaptureField } from '@/settings/components/signature-capture-field';
import { useRegisterPractitionerSave } from '../use-practitioner-save-context';
import type {
  ProfessionalDetails,
  ProfessionalDetailsLanguage,
  ProfessionalDetailsReasonVisit,
  StaffSpeciality,
} from '@/types/settings-api';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Professional Details tab — PROD-761 commits 3 → 9.
 *
 * Wired against GET/POST /api/ProfessionalDetails plus the License
 * Authority master lookup. Field set covered (Flutter parity, in display
 * order from doctor_professional_tab.dart):
 *   - License Number (mandatory, maxLength 50, dart:99)
 *   - License Issuing Authority (mandatory chip-select, dart:188)
 *   - License Expiration Date (mandatory, dart:126)
 *   - Language Spoken (mandatory typeahead, dart:231)
 *   - Professional Statement (mandatory textarea, maxLength 300, dart:367)
 *   - Specialities (mandatory SNOMED typeahead, dart:400)
 *   - Procedural Skills (optional CPT typeahead, dart:555)
 *   - Reasons for Visit (mandatory local CRUD, dart:718)
 *
 * Provider signature (PROD-1051): re-introduced as a draw-OR-upload
 * control (`SignatureCaptureField`) bound to `doctorSignatureUrl`. Flutter's
 * `DoctorSignatureWidget` was upload-only and commented out
 * (doctor_professional_tab.dart:81); we surface it again because the
 * document-centric consent flow auto-sources this signature into the
 * provider-signature box at fill time. The field round-trips via the same
 * `POST /api/ProfessionalDetails` save; upload area `"PRACTICE"` matches
 * Flutter (doctor_signature_widget.dart:166). Optional — not in `isValid`.
 *
 * Wire-shape preserved verbatim (see use-professional-details.ts):
 *   - British spelling: licenceAuthorityId, licenceNo
 *   - expirationDate: no-Z ISO (YYYY-MM-DDTHH:mm:ss.sss)
 *   - Reason-for-visit soft-delete: backend join PKs preserved by setting
 *     `isDeleted: true` on existing rows (do NOT splice the array — the
 *     server uses the PK to mark archived; missing rows would be treated
 *     as no-op rather than deleted).
 *   - Specialities `system` param: per-org config from
 *     userData.resourceBasedIntegration.specialitySystem; demo / dev
 *     orgs ship empty string. See use-speciality-codes-search.ts.
 */
export interface PractitionerProfessionalTabProps {
  doctorSetupId: string;
  canEdit: boolean;
}

export function PractitionerProfessionalTab({
  doctorSetupId,
  canEdit,
}: PractitionerProfessionalTabProps) {
  const { data, isLoading } = useProfessionalDetails(doctorSetupId);
  const { data: authorities = [] } = useLicenseAuthorities();
  const saveMutation = useSaveProfessionalDetails();

  const [source, setSource] = useState<ProfessionalDetails | null>(null);
  const [draft, setDraft] = useState<ProfessionalDetails | null>(null);

  useEffect(() => {
    if (data) {
      // The API returns null (not []) for empty list fields — e.g. `skills: null`
      // for a practitioner with none yet. Coalesce to [] so the tab's .map/.filter/
      // .length calls never hit null (which error-boundaries the whole screen).
      const normalized: ProfessionalDetails = {
        ...data,
        languages: data.languages ?? [],
        speciality: data.speciality ?? [],
        skills: data.skills ?? [],
        providerReasonVisits: data.providerReasonVisits ?? [],
      };
      setSource(normalized);
      setDraft(normalized);
    }
  }, [data]);

  const isDirty = useMemo(() => {
    if (!source || !draft) return false;
    return JSON.stringify(source) !== JSON.stringify(draft);
  }, [source, draft]);

  // Flutter parity (doctor_professional_tab.dart): all fields mandatory
  // except Procedural Skills —
  //   License Number (dart:92), License Issuing Authority (dart:126),
  //   License Expiration (dart:188), Language Spoken (dart:231),
  //   Professional Statement (dart:367), Specialities (dart:400),
  //   Reasons for Visit (dart:718). RFV counts non-deleted entries only —
  //   soft-deleted rows round-trip in the array but don't satisfy it.
  const isValid = useMemo(() => {
    if (!draft) return false;
    const activeReasonCount = draft.providerReasonVisits.filter(
      (r) => !r.isDeleted,
    ).length;
    return (
      draft.licenceNo.trim().length > 0 &&
      !!draft.licenceAuthorityId &&
      !!draft.expirationDate &&
      draft.languages.length > 0 &&
      draft.professionalStatement.trim().length > 0 &&
      draft.speciality.length > 0 &&
      activeReasonCount > 0
    );
  }, [draft]);

  // Keep the live draft/validity/mutation in a ref so `handleSave` can stay a
  // STABLE reference (empty deps). It's registered into the page-chrome Save via
  // useRegisterPractitionerSave below; if its identity changed each render, the
  // registration effect would re-fire setState every render → "Maximum update
  // depth exceeded". (React Query's useMutation result object isn't referentially
  // stable per render, so depending on it here is what caused the loop.)
  const saveStateRef = useRef({ draft, isValid, mutate: saveMutation.mutate });
  saveStateRef.current = { draft, isValid, mutate: saveMutation.mutate };

  const handleSave = useCallback(() => {
    const { draft: current, isValid: valid, mutate } = saveStateRef.current;
    if (!current) return;
    if (!valid) {
      toast.error('All required fields must be completed before saving.');
      return;
    }
    mutate(current, {
      onSuccess: () => toast.success('Professional details saved'),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Save failed'),
    });
  }, []);

  // PROD-897: register this tab's save with the page chrome so the top Save
  // button dispatches the same `handleSave` (mirrors the Account tab — the
  // inline button below stays the primary affordance). Called unconditionally
  // before the early-return — Rules of Hooks.
  useRegisterPractitionerSave(
    canEdit && draft
      ? {
          canSave: isDirty && isValid,
          isPending: saveMutation.isPending,
          save: handleSave,
          label: saveMutation.isPending ? 'Saving…' : 'Save Professional Details',
        }
      : null,
  );

  if (isLoading || !draft) {
    return (
      <BrandLoading size={48} label="Loading professional details…" />
    );
  }

  const setField = <K extends keyof ProfessionalDetails>(
    key: K,
    value: ProfessionalDetails[K],
  ) => {
    setDraft({ ...draft, [key]: value });
  };

  return (
    <div className="space-y-6">
      <h2 className={SETTINGS_SECTION_TITLE}>
        Professional details
      </h2>
      <PlainCard>
        <div>
          <FieldLabel stacked>Provider signature</FieldLabel>
          <SignatureCaptureField
            label="Signature"
            value={draft.doctorSignatureUrl || null}
            onChange={(url) => setField('doctorSignatureUrl', url)}
            readOnly={!canEdit}
          />
          <p data-slot="settings-field-helper" className="mt-2 text-size-sm font-normal text-text-secondary">
            Used to auto-fill the provider signature on consent forms.
          </p>
        </div>

        <div className="border-t border-border-hairline pt-4">
          <FieldLabel stacked>
            License number <span className="text-status-error">*</span>
          </FieldLabel>
          <TextField
            value={draft.licenceNo}
            maxLength={50}
            placeholder="Enter license number"
            disabled={!canEdit}
            onChange={(e) => setField('licenceNo', e.target.value)}
          />
        </div>

        <div>
          <FieldLabel stacked>
            License issuing authority <span className="text-status-error">*</span>
          </FieldLabel>
          {authorities.length === 0 ? (
            <BrandLoading size={48} label="Loading authorities…" />
          ) : (
            <Segmented
              aria-label="License issuing authority"
              // Master-data single-select. `licenceAuthorityId` may be
              // null/empty pre-selection (required but starts blank on a new
              // practitioner record); pass undefined for an unmatched value so
              // no segment renders active until the user picks one.
              value={draft.licenceAuthorityId || undefined}
              onChange={(v) => setField('licenceAuthorityId', v)}
              options={authorities.map((a) => ({
                value: a.Id,
                label: a.Name,
                disabled: !canEdit,
              }))}
            />
          )}
        </div>

        <div>
          <FieldLabel stacked>
            License expiration date <span className="text-status-error">*</span>
          </FieldLabel>
          <DatePickerField
            value={draft.expirationDate ? draft.expirationDate.slice(0, 10) : ''}
            disabled={!canEdit}
            onChange={(v) =>
              setField(
                'expirationDate',
                v ? `${v}T00:00:00.000` : null,
              )
            }
          />
        </div>

        <div>
          <FieldLabel stacked>
            Language spoken <span className="text-status-error">*</span>
          </FieldLabel>
          <LanguagesEditor
            languages={draft.languages}
            canEdit={canEdit}
            onChange={(next) => setField('languages', next)}
          />
        </div>

        <div>
          <FieldLabel stacked>
            Professional statement <span className="text-status-error">*</span>
          </FieldLabel>
          <textarea
            value={draft.professionalStatement}
            // Flutter parity: 300-char cap. The live deployed Flutter app
            // shows an "n/300" counter on this field, so 300 is the
            // user-facing limit in the source-of-truth surface.
            //
            // The HTML `maxLength` attribute is render-time only — it
            // gates growth past the cap on the user's input but does NOT
            // truncate the loaded `value`. So a legacy record with a
            // >300 char statement renders verbatim and survives a no-op
            // re-save; the user can only delete chars to bring it down,
            // or rewrite to ≤300. No silent truncation.
            //
            // Whether the backend column / FHIR schema enforces 300 as
            // a hard cap or just reflects the Flutter convention is not
            // verified — Flutter wins on behavior per CLAUDE.md
            // (§Source-of-truth order), so we match the deployed limit.
            // If backend allows more and product wants React to too, lift
            // this cap and Flutter both, not just React.
            maxLength={300}
            placeholder="e.g. John Smith is a concerned and caring primary care doctor serving the community of Dubai, UAE. Dr. Smith attended the University of Tennessee, where he received his medical degree."
            disabled={!canEdit}
            onChange={(e) =>
              setField('professionalStatement', e.target.value)
            }
            data-slot="settings-textarea"
            className="w-full min-h-25 rounded-lg border border-border-hairline-strong bg-surface-card px-3 py-2 text-sm text-text-default focus:outline-none focus:border-brand-primary disabled:opacity-50"
          />
          <p data-slot="settings-field-helper" className="mt-2 text-size-sm font-normal text-text-secondary">
            Professional Statement is what patients see in your profile, while booking.
          </p>
        </div>

        <div className="border-t border-border-hairline pt-4">
          <FieldLabel stacked>
            Specialities <span className="text-status-error">*</span>
          </FieldLabel>
          <SpecialityPicker
            specialities={draft.speciality}
            canEdit={canEdit}
            onChange={(next) => setField('speciality', next)}
          />
        </div>

        <div className="border-t border-border-hairline pt-4">
          <FieldLabel stacked>Procedural skills</FieldLabel>
          <SkillsEditor
            skills={draft.skills}
            canEdit={canEdit}
            onChange={(next) => setField('skills', next)}
          />
        </div>

        <div className="border-t border-border-hairline pt-4">
          <FieldLabel stacked>
            Reasons for visit <span className="text-status-error">*</span>
          </FieldLabel>
          <ReasonForVisitEditor
            items={draft.providerReasonVisits}
            canEdit={canEdit}
            onChange={(next) => setField('providerReasonVisits', next)}
          />
        </div>
      </PlainCard>

      {canEdit && (
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || !isValid || saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save Professional Details'}
          </Button>
        </div>
      )}
    </div>
  );
}

function PlainCard({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border-hairline bg-surface-card shadow-1 px-6 py-6 flex flex-col gap-6">
      {children}
    </section>
  );
}

/**
 * Languages editor — typeahead picker + chip pills with remove.
 *
 * Wired to `useLanguageSearch` (debounced 250ms). Selecting a language
 * appends it to the draft's `languages` array with `id: null` for the
 * per-practitioner join PK (backend allocates on save). Remove drops
 * the entry from the array.
 *
 * Properties verified during @samer PR #116 review:
 *
 * - **Duplicate guard (both sides covered)**: `attachedIds` is computed
 *   from the `languages` PROP (which is `draft.languages`), so it
 *   always reflects the in-flight state — entries added in the same
 *   session are guarded, not just the original fetch response. The
 *   secondary `languages.some(...)` check inside `handleSelect` is
 *   defence-in-depth against double-clicks.
 * - **`id: null` survives the wire**: api-client.ts uses `JSON.stringify`
 *   which preserves `null` keys (only `undefined` gets stripped). The
 *   POST body ships `id: null` verbatim; backend handles new-entry
 *   allocation server-side.
 * - **AbortSignal on debounced fetch**: `useLanguageSearch` passes
 *   React Query's `signal` into `get()`, which forwards it to fetch.
 *   When the query key changes (new debounced search term) or the
 *   component unmounts, in-flight requests are aborted. Stale
 *   late-arriving responses won't populate the dropdown because React
 *   Query caches by key, not by request timestamp.
 */
function LanguagesEditor({
  languages,
  canEdit,
  onChange,
}: {
  languages: ProfessionalDetailsLanguage[];
  canEdit: boolean;
  onChange: (next: ProfessionalDetailsLanguage[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);
  const { data: results = [], isLoading } = useLanguageSearch(debouncedSearch);

  // Build picker options from the search results. Filter out languages
  // already attached to the practitioner so the user can't accidentally
  // re-add them (selection would no-op anyway, but hiding is cleaner).
  const attachedIds = new Set(languages.map((l) => l.code.code).filter(Boolean));
  const options = results
    .filter((r) => !attachedIds.has(r.code.code))
    .map((r) => ({
      id: r.languageId,
      label: r.code.displayName ?? r.code.code ?? r.languageId,
      secondary: r.code.system ?? undefined,
    }));

  const handleSelect = (languageId: string) => {
    const found = results.find((r) => r.languageId === languageId);
    if (!found) return;
    if (languages.some((l) => l.code.code === found.code.code)) return;
    onChange([
      ...languages,
      {
        id: null,
        languageId: found.languageId,
        code: found.code,
      },
    ]);
    setSearch('');
    setOpen(false);
  };

  const handleRemove = (languageId: string) => {
    onChange(languages.filter((l) => l.languageId !== languageId));
  };

  return (
    <div className="space-y-3">
      {canEdit && (
        <TagSearchField
          value={search}
          placeholder="Enter Language"
          hint="Select language to add."
          open={open}
          onOpenChange={setOpen}
          onSearchChange={setSearch}
          loading={isLoading}
          options={options}
          onSelect={handleSelect}
        />
      )}

      {languages.length === 0 ? (
        <p className="text-sm text-text-muted">No languages added yet.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {languages.map((l) => (
            <Tag
              key={l.languageId}
              onRemove={canEdit ? () => handleRemove(l.languageId) : undefined}
              removeLabel={`Remove ${l.code.displayName ?? 'language'}`}
            >
              {l.code.displayName ?? l.code.code ?? 'Language'}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Skills editor — typeahead + chip pills. Same pattern as LanguagesEditor;
 * see that component's header comment for the verified properties
 * (duplicate guard, id-null wire preservation, AbortSignal cancellation).
 *
 * Source endpoint: `useProcedureCodesSearch` → /api/Master/ProcedureCodesList.
 * Master entries match the `StaffSpeciality` shape exactly, so we can
 * append them to `draft.skills` after stripping the per-row `id` (backend
 * allocates on save). Duplicate guard keys on `specialitySkillId` (the
 * master GUID), which is stable across master refreshes.
 *
 * CPT descriptions can be long (e.g. "Anesthesia for second- and third-
 * degree burn excision or debridement…"); the result list shows the
 * `displayName` in the primary slot and the `code` as secondary so users
 * can scan by short CPT code OR by procedure name.
 */
function SkillsEditor({
  skills,
  canEdit,
  onChange,
}: {
  skills: StaffSpeciality[];
  canEdit: boolean;
  onChange: (next: StaffSpeciality[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);
  const { data: results = [], isLoading } = useProcedureCodesSearch(debouncedSearch);

  const attachedIds = new Set(
    skills.map((s) => s.specialitySkillId).filter(Boolean),
  );
  const options = results
    .filter((r) => !attachedIds.has(r.specialitySkillId))
    .map((r) => ({
      id: r.specialitySkillId,
      label: r.code.displayName ?? r.code.code ?? r.specialitySkillId,
      secondary: r.code.code ?? undefined,
    }));

  const handleSelect = (specialitySkillId: string) => {
    const found = results.find((r) => r.specialitySkillId === specialitySkillId);
    if (!found) return;
    if (skills.some((s) => s.specialitySkillId === found.specialitySkillId)) return;
    onChange([
      ...skills,
      {
        specialitySkillId: found.specialitySkillId,
        code: found.code,
      },
    ]);
    setSearch('');
    setOpen(false);
  };

  const handleRemove = (specialitySkillId: string) => {
    onChange(skills.filter((s) => s.specialitySkillId !== specialitySkillId));
  };

  return (
    <div className="space-y-3">
      {canEdit && (
        <TagSearchField
          value={search}
          placeholder="Enter procedural skills"
          hint="Type to search and then add."
          open={open}
          onOpenChange={setOpen}
          onSearchChange={setSearch}
          loading={isLoading}
          options={options}
          onSelect={handleSelect}
        />
      )}

      {skills.length === 0 ? (
        <p className="text-sm text-text-muted">No skills added yet.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {skills.map((s) => (
            <Tag
              key={s.specialitySkillId}
              onRemove={
                canEdit ? () => handleRemove(s.specialitySkillId) : undefined
              }
              removeLabel={`Remove ${s.code.displayName ?? 'skill'}`}
            >
              <span className="truncate max-w-70" title={s.code.displayName ?? undefined}>
                {s.code.displayName ?? s.code.code ?? 'Skill'}
              </span>
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Reason-for-Visit editor — PROD-761 commit 7. Local CRUD only.
 *
 * No typeahead: Flutter ships free-text via `add_reason_for_visit.dart`,
 * stored under `providerReasonVisits` with `display` carrying the user's
 * entry and `code` / `system` left null (the optional SNOMED join is
 * never reached from the practitioner edit surface — that lives in the
 * scheduling reason-master, not here).
 *
 * Soft-delete semantics (do NOT change without backend coordination):
 *   - New row: `{ id: null, code: null, display: text, system: null, isDeleted: false }`.
 *     Backend allocates the join PK on save; `id: null` survives the wire
 *     because api-client.ts uses `JSON.stringify` (only `undefined` is stripped).
 *   - Existing row removal: flip `isDeleted: true` on the row instead of
 *     splicing it. The .NET save handler keys on the join PK to mark the
 *     row archived; if the row is missing from the POST body it's treated
 *     as no-op rather than deleted, which silently drops the user intent.
 *   - New rows the user added then removed: those have `id === null`, so
 *     they can be filtered out instead of soft-deleted (nothing to archive).
 */
function ReasonForVisitEditor({
  items,
  canEdit,
  onChange,
}: {
  items: ProfessionalDetailsReasonVisit[];
  canEdit: boolean;
  onChange: (next: ProfessionalDetailsReasonVisit[]) => void;
}) {
  const [draftText, setDraftText] = useState('');

  // Render only non-deleted rows. Keep the original index so add / remove
  // operations can target the canonical array (which still holds the
  // soft-deleted entries that need to round-trip to the server).
  const visibleEntries = items
    .map((entry, originalIndex) => ({ entry, originalIndex }))
    .filter(({ entry }) => !entry.isDeleted);

  const handleAdd = () => {
    const text = draftText.trim();
    if (!text) return;
    // Flutter parity (doctor_professional_tab.dart:761-787): hard cap at
    // 10 active rows before the duplicate check, both error strings
    // verbatim from Flutter (copied per the magic-strings rule in
    // CLAUDE.md → Flutter → React Conversion Contract).
    if (visibleEntries.length >= 10) {
      toast.error('You cannot add more than 10 RFV');
      return;
    }
    const exists = visibleEntries.some(
      ({ entry }) =>
        (entry.display ?? '').trim().toLowerCase() === text.toLowerCase(),
    );
    if (exists) {
      toast.error('You cannot add Same RFV');
      return;
    }
    onChange([
      ...items,
      {
        id: null,
        code: null,
        display: text,
        system: null,
        isDeleted: false,
      },
    ]);
    setDraftText('');
  };

  const handleRemove = (originalIndex: number) => {
    const target = items[originalIndex];
    if (!target) return;
    if (target.id === null) {
      // Unsaved new entry — just drop it.
      onChange(items.filter((_, i) => i !== originalIndex));
      return;
    }
    // Persisted row — flip the soft-delete flag so the backend archives it.
    onChange(
      items.map((row, i) =>
        i === originalIndex ? { ...row, isDeleted: true } : row,
      ),
    );
  };

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="flex items-start gap-2">
          <TextField
            value={draftText}
            placeholder="Enter reason for visit"
            // Flutter parity: maxLength=100 on the add input
            // (doctor_professional_tab.dart:759). Matches the backend
            // column cap; an unbounded string would surface a raw 500
            // rather than a graceful validation message.
            maxLength={100}
            onChange={(e) => setDraftText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={handleAdd}
            disabled={draftText.trim().length === 0}
            className="shrink-0"
          >
            <Plus className="size-4 mr-2" />
            Add
          </Button>
        </div>
      )}

      {visibleEntries.length === 0 ? (
        <p className="text-sm text-text-muted">No reasons added yet.</p>
      ) : (
        <ul className="space-y-2">
          {visibleEntries.map(({ entry, originalIndex }) => (
            <li
              key={entry.id ?? `reason-new-${originalIndex}`}
              className="flex items-center justify-between gap-2 rounded-md border border-border-hairline bg-surface-card px-3 py-2"
            >
              <span className="text-sm text-text-default">
                {entry.display ?? '—'}
              </span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleRemove(originalIndex)}
                  aria-label={`Remove ${entry.display ?? 'reason'}`}
                  className="icon-action"
                >
                  <Trash2 className="size-4" aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Specialities picker extracted to the shared `SpecialityPicker`
// (src/settings/components/speciality-picker.tsx) so the Staff Account tab
// (PROD-788) consumes the identical control. See that file for the
// SNOMED-search + duplicate-guard + wire notes.
